<template>
  <div
    class="relative overflow-hidden rounded-[8px] bg-[#2060f0] text-white box-border"
    :class="
      isDesktop
        ? 'flex items-center justify-center gap-10 px-16 py-6'
        : 'flex flex-col items-center justify-center gap-4 px-4 py-6'
    "
    data-name="cross_sell_banner"
    :data-size="isDesktop ? 'medium' : 'small'"
  >
    <div
      class="absolute inset-0 bg-cover pointer-events-none"
      :style="{
        backgroundImage: `url(${
          isDesktop
            ? '/travelist/pc/img/bg_crosssell_banner.jpg'
            : '/travelist/sp/img/bg_crosssell_banner_sp.jpg'
        })`,
        backgroundPosition: isDesktop ? 'center 80%' : 'center',
      }"
      aria-hidden="true"
    ></div>
    <div
      class="absolute inset-0 bg-[rgba(4,76,210,0.85)] pointer-events-none"
      aria-hidden="true"
    ></div>
    <div
      class="relative flex flex-col items-center text-center"
      :class="isDesktop ? 'flex-1 gap-10' : 'w-full gap-6'"
    >
      <div
        class="flex flex-col items-center"
        :class="isDesktop ? 'gap-6' : 'gap-2'"
      >
        <p class="leading-4" :class="isDesktop ? 'text-xl' : 'text-sm'">
          決済から5日間だけの期間限定セール
        </p>
        <p data-name="discount_headline" class="whitespace-nowrap">
          <span
            v-for="(segment, i) in headlineSegments"
            :key="i"
            class="font-bold underline decoration-solid decoration-[#f7c53c]"
            :data-name="segment.dataName"
            :class="isDesktop ? segment.pc : segment.sp"
            >{{ segment.text }}</span
          >
        </p>
      </div>
      <ul class="flex w-full flex-col gap-2 text-left">
        <li
          class="flex items-start gap-2"
          :class="isDesktop ? 'text-sm leading-5' : 'text-xs leading-4'"
        >
          <span>※</span
          ><span>航空券決済完了の方のみにご案内しております。</span>
        </li>
        <li
          class="flex items-start gap-2"
          :class="isDesktop ? 'text-sm leading-5' : 'text-xs leading-4'"
        >
          <span>※</span
          ><span
            >割引率は変動します。実際の料金はホテル選択後に確認できます。</span
          >
        </li>
      </ul>
    </div>
    <div
      class="relative flex flex-col items-center"
      :class="isDesktop ? 'gap-4' : 'gap-2'"
    >
      <p
        class="text-center font-bold leading-6 whitespace-nowrap"
        :class="isDesktop ? 'text-xl w-[320px]' : 'text-sm w-[280px]'"
        v-html="catchText"
      ></p>
      <div :class="isDesktop ? 'w-[320px]' : 'w-[280px]'">
        <Button
          variant="cta"
          tag="a"
          :href="linkUrl"
          :size="isDesktop ? 'medium' : 'small'"
          icon-position="right"
          block
          data-name="cross_sell_cta"
          data-gtm-event-click="click_tx-complete_xsell_btn"
          data-gtm-event-imp="imp_tx-complete_xsell_btn"
        >
          {{ linkText }}
          <template #icon>
            <i class="fa-solid fa-chevron-right"></i>
          </template>
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// isDesktop はグローバル mixin（src/main.js=true, src_sp/main.js=false）を
// テンプレートで直接参照する（CompleteStepper.vue / AppDownloadBanner.vue と同様のパターン）。
// 表示するか否かの判定ロジック（isShowCrossSellLink 等）は持たない。呼び出し元
// （payment_completion_screen.js からのブリッジ、design.md 要件13.4/2.6）が
// マウント点の有無・setCrossSellData 経由で表示を制御する。渡された props は
// そのまま描画するのみの静的な visual コンポーネント。
// Figma after 版(PC node 1811-64477 / SP node 1811-64545)はホテル一覧の個別
// カード表示を持たないため、hotelList 相当の props は設けない(要件 13.5)。
// 背景写真(AdobeStock_95358673)は public_html/travelist/{pc,sp}/img/ に実アセットとして配置し
// (他コンポーネントの `/travelist/...` 絶対パス+background-image と同じ慣習)、
// Figma仕様の青オーバーレイ rgba(4,76,210,0.85) を別レイヤーで重ねている。
// PC の background-position を 80% にしているのは、Figma node 1811-64477 の
// 画像配置(top: calc(50% - 165.5px)、コンテナ高208pxに対し画像高757px)を
// 逆算した値。center(50%)のままだと海側の景色しか映らず、Figma が意図する
// 木造建築側の下寄りクロップとずれる。SP(node 1811-64545)は元々ほぼ中央
// クロップのため center のまま。
import { computed } from 'vue';
import Button from '~/src_common/components/parts/button/Button.vue';

const props = defineProps<{
  discountPercent: number;
  linkText: string;
  linkUrl: string;
  catchText: string;
}>();

// discount_headline の4セグメント（テキスト＋PC/SPそれぞれのFigma準拠フォントサイズ・
// 下線太さ）。数値のみが動的(discountPercent)で、他はFigma固定テキスト。
const headlineSegments = computed(() => [
  {
    text: '宿泊料金が最大',
    pc: 'text-[48px] leading-[32px] decoration-[8px]',
    sp: 'text-[24px] leading-[32px] decoration-[3px]',
  },
  {
    text: props.discountPercent,
    dataName: 'discount_percent',
    pc: 'text-[60px] leading-[32px] decoration-[8px]',
    sp: 'text-[32px] leading-[32px] decoration-[3px]',
  },
  {
    text: '%',
    pc: 'text-[48px] leading-[32px] decoration-[8px]',
    sp: 'text-[20px] leading-[32px] decoration-[3px]',
  },
  {
    text: 'オフ!',
    pc: 'text-[48px] leading-[32px] decoration-[8px]',
    sp: 'text-[24px] leading-[32px] decoration-[3px]',
  },
]);
</script>
